import { showConfirm } from '../components/ConfirmDialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useConfigLoadError } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from './../hooks/useProfileContext';
import { apiGetTeam } from '../api';
import type { HospitalityConfig, DietType, TransportType } from '../types';
import { createDefaultRiderConfig } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import { useFocusField } from '../hooks/useFocusField';
import AutoTextarea from '../components/AutoTextarea';
import PageLoadError from '../components/PageLoadError';
import { useSuggestionList } from '../hooks/useSuggestionList';
import { linesToText, textToLines } from '../lib/multiline';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';

const DIET_LABELS: Record<DietType, string> = {
  none: 'configHospitality.dietNone',
  vegetarian: 'configHospitality.dietVegetarian',
  vegan: 'configHospitality.dietVegan',
  gluten_free: 'configHospitality.dietGlutenFree',
  lactose_free: 'configHospitality.dietLactoseFree',
  nut_allergy: 'configHospitality.dietNutAllergy',
  other: 'configHospitality.dietOther',
};

export default function ConfigHospitalityPage() {
  const { config, updateHospitality, reload } = useConfig();
  // Lot 3a — this page reads its data through `useConfig` (the band's
  // rider_config). The hook never wipes state on a failed load, so without a
  // guard the page would render EMPTY FIELDS as if the band had nothing typed:
  // a silent void that reads as data loss. The shared state replaces the whole
  // page when the config could not be read, and its Retry re-runs the hook's own
  // `reload` (never a hard page reload).
  const configLoad = useConfigLoadError();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { t } = useTranslation();
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  const { activeProfileId } = useProfileContext();
  const [transport, setTransport] = useState<TransportType>('van');
  useFocusField(true);
  // Reorder the Hospitality fields to mirror the Rider Layout's `hospitality.*`
  // order. The auto-calculated block stays pinned at the top; Various and the
  // Diet suggestions stay pinned at the bottom.
  const layoutOrder = useLayoutOrder('hospitality');
  const { types: dietTypes, put: putDietTypes } = useSuggestionList('diet-types');
  const [newDietType, setNewDietType] = useState('');

  // Editable app-wide diet type suggestions.
  const addDietType = () => {
    const t = newDietType.trim();
    if (!t) return;
    if (!dietTypes.some((x) => x.toLowerCase() === t.toLowerCase())) void putDietTypes([...dietTypes, t]);
    setNewDietType('');
  };
  const removeDietType = (t: string) => void putDietTypes(dietTypes.filter((x) => x !== t));

  // Diet now lives on the Team identity (team_members), not the per-band rider
  // cards. Resolve id -> diet from the Team registry scoped to this band.
  const [dietById, setDietById] = useState<Record<string, { diet?: string; dietNotes?: string }>>({});
  useEffect(() => {
    if (!activeProfileId) { setDietById({}); return; }
    let cancelled = false;
    apiGetTeam({ band: activeProfileId }).then((members) => {
      if (cancelled) return;
      const map: Record<string, { diet?: string; dietNotes?: string }> = {};
      for (const m of members) map[m.id] = { diet: m.diet || '', dietNotes: m.diet_notes || '' };
      setDietById(map);
    }).catch(() => { if (!cancelled) setDietById({}); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  const cfg = config.hospitality[transport];

  // Defensive fallback: older rider_config JSONs (pre-identity-consolidation)
  // have no `rider` key — default to an empty selection instead of crashing.
  const riderCfg = config.rider ?? createDefaultRiderConfig();

  const selectedMusicians = musicians.filter(m =>
    riderCfg.activeMusicianIds.includes(m.id)
  );

  const allCrew = crew || [];
  const selectedCrew = allCrew.filter(c => riderCfg.activeCrewIds?.includes(c.id));
  const crewCount = selectedCrew.reduce((sum, c) => sum + (c.count || 0), 0);
  const totalPeople = selectedMusicians.length + crewCount;

  const dietMap = new Map<DietType, { name: string; note: string }[]>();
  for (const m of selectedMusicians) {
    const diet = (dietById[m.id]?.diet as DietType) || m.diet;
    if (diet && diet !== 'none') {
      const existing = dietMap.get(diet) || [];
      existing.push({ name: m.name, note: dietById[m.id]?.dietNotes || m.dietNotes || '' });
      dietMap.set(diet, existing);
    }
  }
  for (const c of selectedCrew) {
    const diet = (dietById[c.id]?.diet as DietType) || c.diet;
    if (diet && diet !== 'none') {
      const existing = dietMap.get(diet) || [];
      existing.push({ name: c.name || c.role, note: dietById[c.id]?.dietNotes || c.dietNotes || '' });
      dietMap.set(diet, existing);
    }
  }

  // ⚠️ FIX 2026-10-17 (user : « le nombre de dressing room towels est calculé faux ») :
  // les totaux de la config utilisaient `|| 0` (un undefined comptait 0) pendant que
  // le rider PDF utilise `?? 1` (fallback défaut v0.636 : musiciens 1 stage + 1
  // dressing, crew 0 + 1) — les 2 surfaces divergeaient. Aligné sur le PDF : `??`
  // (seul le champ ABSENT retombe au défaut ; un 0 explicite reste 0).
  const totalTowels = selectedMusicians.reduce((sum, m) => sum + (m.towels ?? 1), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.towels ?? 0) * (c.count || 1), 0);
  const totalDressingTowels = selectedMusicians.reduce((sum, m) => sum + (m.dressingRoomTowels ?? 1), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.dressingRoomTowels ?? 1) * (c.count || 1), 0);
  const totalWater = selectedMusicians.reduce((sum, m) => sum + (m.water ?? 0), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.water ?? 0) * (c.count || 1), 0);

  const handleChange = (key: keyof HospitalityConfig, value: any) => {
    updateHospitality({
      [transport]: { ...cfg, [key]: value },
    } as any);
  };

  const populateAll = async (src: TransportType) => {
    const srcCfg = config.hospitality[src];
    if (!(await showConfirm(t('configHospitality.populateAllConfirm', { src })))) return;
    updateHospitality({ [transport]: { ...srcCfg } } as any);
  };

  const populateField = (key: keyof HospitalityConfig) => async (src: TransportType) => {
    const srcVal = config.hospitality[src][key];
    if (JSON.stringify(srcVal) === JSON.stringify(cfg[key])) return;
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  // Each Rider Layout field (`hospitality.*`) is a reorderable block; Catering,
  // Drinks and Security became layout fields on 2026-09-16, so they are ordered
  // by the layout as well. `sortByLayoutOrder` keeps the default relative order
  // for blocks whose id is absent from the layout.
  const blocks = [
    {
      id: 'hospitality.non-food-allergies',
      render: () => (
        <div className="form-section" data-field-id="hospitality.non-food-allergies">
          <h2>{t('configHospitality.nonFoodAllergies')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.nonFoodAllergiesLabel')} current={transport} fieldId="hospitality.non-food-allergies" exampleText="lead singer has STRONG allergies to cats and dogs — no cats/dogs in backstage, dressing rooms or catering spaces." onUseExample={(t) => handleChange('nonFoodAllergies', t)} onPopulateFrom={populateField('nonFoodAllergies')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.nonFoodAllergies || ''}
              onChange={(e) => handleChange('nonFoodAllergies', e.target.value)}
              placeholder={'e.g. lead singer has STRONG allergies to cats and dogs — no cats/dogs in backstage, dressing rooms or catering spaces.'}
              rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.dressing-rooms',
      render: () => (
        <div className="form-section" data-field-id="hospitality.dressing-rooms">
          <h2>{t('configHospitality.dressingRooms')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.dressingRoomsLabel')} current={transport} fieldId="hospitality.dressing-rooms" exampleText="2 private dressing rooms with light mirrors, clothes stands, iron + ironing board" onUseExample={(t) => handleChange('dressingRooms', t)} onPopulateFrom={populateField('dressingRooms')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.dressingRooms}
              onChange={(e) => handleChange('dressingRooms', e.target.value)}
              placeholder="e.g. 2 private dressing rooms with light mirrors, clothes stands, iron + ironing board"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.security',
      render: () => (
        <div className="form-section" data-field-id="hospitality.security">
          <h2>{t('configHospitality.security')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.securityLabel')} current={transport} fieldId="hospitality.security" exampleText="The promoter is responsible at all times for the safety of the artists and their equipment at the venue. Promoter must provide adequate liability and equipment insurance." onUseExample={(t) => handleChange('security', t)} onPopulateFrom={populateField('security')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.security || ''}
              onChange={(e) => handleChange('security', e.target.value)}
              placeholder={t('configHospitality.securityPh')}
              rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.breakfast',
      render: () => (
        <div className="form-section" data-field-id="hospitality.breakfast">
          <h2>{t('configHospitality.breakfast')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.breakfastLabel')} current={transport} fieldId="hospitality.breakfast" exampleText={'Continental breakfast for the crew before load-in\n- Coffee, tea, milk\n- Bread, butter, jam\n- Muesli, yoghurt\n- Juice & fruit'} onUseExample={(t) => handleChange('breakfast', t)} onPopulateFrom={populateField('breakfast')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={cfg.breakfast || ''}
             onChange={(e) => handleChange('breakfast', e.target.value)}
             placeholder={`e.g. Continental breakfast for the crew before load-in\n- Coffee, tea, milk\n- Bread, butter, jam\n- Muesli, yoghurt\n- Juice & fruit`}
             rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.lunch',
      render: () => (
        <div className="form-section" data-field-id="hospitality.lunch">
          <h2>{t('configHospitality.lunch')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.lunchLabel')} current={transport} fieldId="hospitality.lunch" exampleText={'Buffet lunch for the crew between soundcheck and show\n- Hot dish (2 options, one vegetarian)\n- Salad bar\n- Bread, fruit\n- Water & soft drinks'} onUseExample={(t) => handleChange('lunch', t)} onPopulateFrom={populateField('lunch')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={cfg.lunch || ''}
             onChange={(e) => handleChange('lunch', e.target.value)}
             placeholder={`e.g. Buffet lunch for the crew between soundcheck and show\n- Hot dish (2 options, one vegetarian)\n- Salad bar\n- Bread, fruit\n- Water & soft drinks`}
             rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.dinner',
      render: () => (
        <div className="form-section" data-field-id="hospitality.dinner">
          <h2>{t('configHospitality.dinner')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.dinnerLabel')} current={transport} fieldId="hospitality.dinner" exampleText={'Hot catered meal in a quiet room before the show\n- Dietary options labelled (vegetarian, allergies)\n- Water & soft drinks on the table\n- Served before doors open'} onUseExample={(t) => handleChange('dinner', t)} onPopulateFrom={populateField('dinner')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={cfg.dinner || ''}
             onChange={(e) => handleChange('dinner', e.target.value)}
             placeholder={`e.g. Hot catered meal in a quiet room before the show
- Dietary options labelled (vegetarian, allergies)
- Water & soft drinks on the table
- Served before doors open`}
             rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.catering',
      render: () => (
        <div className="form-section" data-field-id="hospitality.catering">
          <h2>{t('configHospitality.catering')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.cateringLabel')} current={transport} fieldId="hospitality.catering" exampleText={'Backstage catering for the whole party\n- Fresh fruit\n- Snacks (chips, cookies, chocolate)\n- Kettle + tea bags\n- Coffee (espresso)'} onUseExample={(t) => handleChange('catering', textToLines(t))} onPopulateFrom={populateField('catering')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={linesToText(cfg.catering)}
             onChange={(e) => handleChange('catering', textToLines(e.target.value))}
             placeholder={'e.g. Backstage catering for the whole party\n- Fresh fruit\n- Snacks (chips, cookies, chocolate)\n- Kettle + tea bags\n- Coffee (espresso)'}
             rows={8}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'hospitality.drinks',
      render: () => (
        <div className="form-section" data-field-id="hospitality.drinks">
          <h2>{t('configHospitality.drinks')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.drinksLabel')} current={transport} fieldId="hospitality.drinks" exampleText={'Dressing-room drinks\n- Still & sparkling water\n- Soft drinks\n- A few beers\n- Coffee & tea'} onUseExample={(t) => handleChange('drinks', textToLines(t))} onPopulateFrom={populateField('drinks')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={linesToText(cfg.drinks)}
             onChange={(e) => handleChange('drinks', textToLines(e.target.value))}
             placeholder={'e.g. Dressing-room drinks\n- Still & sparkling water\n- Soft drinks\n- A few beers\n- Coffee & tea'}
             rows={10}
            />
          </ConfigField>
        </div>
      ),
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);

  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead (the writes stay
  // gated by `useConfig` skipSave, so retrying cannot wipe anything).
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configHospitality.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div className="page config-page" data-config-page="hospitality" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configHospitality.title"
        title={t('configHospitality.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {/* P3c: the auto-calc panel keeps its callout role, in Roadcase tokens
              (amber tint/line instead of the legacy indigo). */}
        <div className="form-section" style={{ backgroundColor: 'var(--accent-tint)', border: '1px solid var(--accent-line)', borderRadius: 'var(--radius)', padding: '16px' }}>
          <h2 style={{ color: 'var(--accent)' }}>{t('configHospitality.autoCalc')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: 26 }}>{totalPeople}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{t('configHospitality.totalPeople', { musicians: selectedMusicians.length, crew: crewCount })}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: 26 }}>{totalPeople}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{t('configHospitality.meals')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: 26 }}>{totalTowels}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{t('configHospitality.stageTowels')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: 26 }}>{totalDressingTowels}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{t('configHospitality.dressingTowels')}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', minWidth: 26 }}>{totalWater}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{t('configHospitality.waterBottles')}</span>
            </div>
          </div>

          {dietMap.size > 0 && (
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: 'var(--font-xs)', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{t('configHospitality.dietsAllergies')}</label>
              <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '12px' }}>
                {Array.from(dietMap.entries()).map(([diet, people]) => (
                  <li key={diet}>
                    <strong>{people.length} × {t(DIET_LABELS[diet] || diet)}</strong>: {people.map((p) => p.note ? `${p.name} (${p.note})` : p.name).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {sortedBlocks.map((b) => b.render())}

        <div className="form-section">
          <h2>{t('configHospitality.various')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configHospitality.variousLabel')} current={transport} fieldId="hospitality.various" onPopulateFrom={populateField('various')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
             value={cfg.various || ''}
             onChange={(e) => handleChange('various', e.target.value)}
             placeholder={t('configHospitality.variousPh')}
             rows={3}
            />
          </ConfigField>
        </div>

        <div className="form-section">
          <h2>{t('configHospitality.dietSuggestions')}</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0 0 8px' }}>
            {t('configHospitality.dietSuggestionsHint')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {dietTypes.map((d) => (
              <span key={d} className="rc-pill" style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: 'var(--font-xs)',
              }}>
                {d}
                <button disabled={!canEditContent} type="button" aria-label={t('configHospitality.removeDiet', { name: d })} onClick={() => removeDietType(d)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, fontSize: 12 }}>×</button>
              </span>
            ))}
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <input disabled={!canEditContent}
                className="form-input"
                style={{ width: 160 }}
                value={newDietType}
                placeholder={t('configHospitality.addDiet')}
                onChange={(e) => setNewDietType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDietType(); } }}
              />
              <button disabled={!canEditContent} className="btn btn-secondary btn-small" onClick={addDietType}>+</button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
